/**
 * Popup for local map pins with notes.
 */

import { formatCoords, googleMapsUrl, copyCoords } from './candidate-popup.js';

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * @param {object} pin
 */
export function buildPinPopupHtml(pin) {
    const coords = formatCoords(pin.lat, pin.lon);
    const savedAt = pin.updatedAt
        ? new Date(pin.updatedAt).toLocaleString()
        : '';

    return `
        <div class="pa-pin-popup">
            <div class="pa-popup-drag-handle d-flex justify-content-between align-items-start gap-2 mb-2" title="Drag to move">
                <div>
                    <div class="small fw-semibold">Map pin</div>
                    <div class="small text-muted">Saved on this device only</div>
                </div>
                <button type="button" class="btn-close btn-close-sm" data-action="close" aria-label="Close"></button>
            </div>
            <div class="mb-2">
                <div class="small text-muted">Location</div>
                <code class="pa-coord-string">${escapeHtml(coords)}</code>
            </div>
            <label class="form-label small mb-1" for="pinPopupNote">Note</label>
            <textarea id="pinPopupNote" class="form-control form-control-sm mb-2" rows="3"
                placeholder="Parking spot, access gate, field notes...">${escapeHtml(pin.note || '')}</textarea>
            ${savedAt ? `<div class="small text-muted mb-2">Updated ${escapeHtml(savedAt)}</div>` : ''}
            <div class="d-flex flex-wrap gap-2">
                <button type="button" class="btn btn-sm btn-primary" data-action="save">Save note</button>
                <button type="button" class="btn btn-sm btn-outline-primary" data-action="copy">Copy lat/lon</button>
                <a class="btn btn-sm btn-outline-success" href="${googleMapsUrl(pin.lat, pin.lon)}" target="_blank" rel="noopener">Google Maps</a>
                <button type="button" class="btn btn-sm btn-outline-danger ms-auto" data-action="delete">Delete pin</button>
            </div>
        </div>
    `;
}

/**
 * @param {HTMLElement} container
 * @param {object} pin
 * @param {{
 *   onSave?: (note: string) => void,
 *   onDelete?: () => void,
 *   onCopy?: (text: string) => void,
 *   onClose?: () => void,
 * }} handlers
 */
export function mountPinPopup(container, pin, handlers = {}) {
    container.innerHTML = buildPinPopupHtml(pin);

    container.querySelector('[data-action="close"]')?.addEventListener('click', () => {
        handlers.onClose?.();
    });

    container.querySelector('[data-action="save"]')?.addEventListener('click', () => {
        const note = container.querySelector('#pinPopupNote')?.value?.trim() || '';
        handlers.onSave?.(note);
    });

    container.querySelector('[data-action="delete"]')?.addEventListener('click', () => {
        handlers.onDelete?.();
    });

    const copyBtn = container.querySelector('[data-action="copy"]');
    if (copyBtn) {
        copyBtn.addEventListener('click', async () => {
            const text = await copyCoords(pin.lat, pin.lon);
            handlers.onCopy?.(text);
            copyBtn.textContent = 'Copied!';
            setTimeout(() => {
                copyBtn.textContent = 'Copy lat/lon';
            }, 1500);
        });
    }
}
